import { useState, useRef, useCallback, useEffect } from "react";
import { Slider } from "../components/ui/slider";
import { toast } from "sonner";
import { Play, Pause, Download, RotateCcw, Loader2, Zap } from "lucide-react";

// Encode AudioBuffer to WAV blob
function encodeWav(audioBuffer) {
  const numCh = audioBuffer.numberOfChannels;
  const len = audioBuffer.length;
  const sr = audioBuffer.sampleRate;
  const buf = new ArrayBuffer(44 + len * numCh * 2);
  const v = new DataView(buf);
  const w = (o, s) => { for (let i = 0; i < s.length; i++) v.setUint8(o + i, s.charCodeAt(i)); };
  w(0, "RIFF"); v.setUint32(4, 36 + len * numCh * 2, true); w(8, "WAVE");
  w(12, "fmt "); v.setUint32(16, 16, true); v.setUint16(20, 1, true);
  v.setUint16(22, numCh, true); v.setUint32(24, sr, true);
  v.setUint32(28, sr * numCh * 2, true); v.setUint16(32, numCh * 2, true); v.setUint16(34, 16, true);
  w(36, "data"); v.setUint32(40, len * numCh * 2, true);
  let off = 44;
  for (let i = 0; i < len; i++) {
    for (let ch = 0; ch < numCh; ch++) {
      const s = Math.max(-1, Math.min(1, audioBuffer.getChannelData(ch)[i]));
      v.setInt16(off, s < 0 ? s * 0x8000 : s * 0x7FFF, true);
      off += 2;
    }
  }
  return new Blob([buf], { type: "audio/wav" });
}

const DEFAULTS = {
  formantShift: [0],
  brightness: [0],
  bodyWeight: [0],
  speed: [1.0],
  emphasis: [0],
  clarity: [0],
  sibilance: [0],
  pitch: [0],
  breathiness: [0],
  raspiness: [0],
  compression: [0],
  volume: [100],
};

const PRESETS = [
  { id: "raspy", label: "Raspy", color: "#FF6B6B", desc: "Rough, textured vocal grit",
    values: { ...DEFAULTS, raspiness: [55], bodyWeight: [3], brightness: [-2], clarity: [4], compression: [30], volume: [105] } },
  { id: "gravelly", label: "Gravelly", color: "#A78BFA", desc: "Deep, low rumble texture",
    values: { ...DEFAULTS, raspiness: [40], bodyWeight: [8], formantShift: [-4], pitch: [-2], brightness: [-4], compression: [45], volume: [110] } },
  { id: "screechy", label: "Screechy", color: "#F59E0B", desc: "Sharp, piercing high edge",
    values: { ...DEFAULTS, formantShift: [6], brightness: [8], sibilance: [6], clarity: [8], raspiness: [25], pitch: [3], volume: [90] } },
  { id: "comedic", label: "Comedic", color: "#1ABC9C", desc: "Exaggerated, cartoonish tone",
    values: { ...DEFAULTS, formantShift: [8], pitch: [5], speed: [1.15], brightness: [4], emphasis: [60], clarity: [6], volume: [110] } },
  { id: "smooth", label: "Smooth", color: "#38BDF8", desc: "Silky, warm radio voice",
    values: { ...DEFAULTS, bodyWeight: [5], brightness: [-3], clarity: [3], sibilance: [-4], compression: [50], breathiness: [10], volume: [105] } },
  { id: "whisper", label: "Whisper", color: "#EC4899", desc: "Soft, intimate breathy tone",
    values: { ...DEFAULTS, breathiness: [50], volume: [85], brightness: [-4], bodyWeight: [-2], compression: [15], clarity: [-3], speed: [0.92] } },
  { id: "villain", label: "Villain", color: "#9D00FF", desc: "Dark, menacing authority",
    values: { ...DEFAULTS, pitch: [-4], formantShift: [-6], bodyWeight: [10], raspiness: [20], brightness: [-5], compression: [60], emphasis: [50], volume: [115] } },
  { id: "robotic", label: "Robotic", color: "#6EE7B7", desc: "Flat, processed digital feel",
    values: { ...DEFAULTS, formantShift: [3], clarity: [10], sibilance: [4], compression: [80], breathiness: [0], raspiness: [15], emphasis: [80], brightness: [3] } },
  { id: "breathe", label: "Breathe", color: "#FB923C", desc: "Airy, relaxed natural breath",
    values: { ...DEFAULTS, breathiness: [35], volume: [90], brightness: [-2], bodyWeight: [2], compression: [10], clarity: [2], speed: [0.95] } },
];

const Section = ({ title, color, children }) => (
  <div className="rounded-xl bg-white/[0.02] border border-white/5 p-3.5 space-y-3">
    <span className="text-[10px] font-bold uppercase tracking-wider block" style={{ color }}>{title}</span>
    {children}
  </div>
);

const Knob = ({ label, value, onChange, min, max, step, unit, color, testId }) => (
  <div>
    <div className="flex justify-between items-center mb-2">
      <span className="text-white/55 text-[11px] font-medium">{label}</span>
      <span className="text-[11px] font-bold tabular-nums" style={{ color }}>
        {value[0] > 0 && (unit === "dB" || unit === "st") ? "+" : ""}{value[0]}{unit}
      </span>
    </div>
    <Slider data-testid={testId} value={value} onValueChange={onChange} min={min} max={max} step={step} />
  </div>
);

export default function VoiceBlendControls({ audioUrl, onProcessed, fileName }) {
  const [params, setParams] = useState(DEFAULTS);
  const [activePreset, setActivePreset] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [decodedBuffer, setDecodedBuffer] = useState(null);

  const ctxRef = useRef(null);
  const sourceRef = useRef(null);

  useEffect(() => {
    if (!audioUrl) { setDecodedBuffer(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const resp = await fetch(audioUrl);
        const arrayBuf = await resp.arrayBuffer();
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        const decoded = await ctx.decodeAudioData(arrayBuf);
        if (!cancelled) setDecodedBuffer(decoded);
        ctx.close();
      } catch (e) { console.error("Failed to decode audio:", e); }
    })();
    return () => { cancelled = true; };
  }, [audioUrl]);

  const set = (key) => (val) => { setParams(p => ({ ...p, [key]: val })); setActivePreset(null); };
  const resetAll = () => { stopPreview(); setParams(DEFAULTS); setActivePreset(null); toast.success("Reset to defaults"); };
  const applyPreset = (preset) => { setParams(preset.values); setActivePreset(preset.id); toast.success(`${preset.label} preset applied`); };

  const buildGraph = useCallback((ctx, buffer) => {
    const p = params;
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    const pitchRate = Math.pow(2, p.pitch[0] / 12);
    source.playbackRate.value = pitchRate * p.speed[0];

    const lowShelf = ctx.createBiquadFilter();
    lowShelf.type = "lowshelf"; lowShelf.frequency.value = 250; lowShelf.gain.value = p.bodyWeight[0];

    const formant1 = ctx.createBiquadFilter();
    formant1.type = "peaking"; formant1.frequency.value = 1000 + p.formantShift[0] * 80; formant1.Q.value = 1.5;
    formant1.gain.value = Math.abs(p.formantShift[0]) > 0 ? 3 : 0;

    const formant2 = ctx.createBiquadFilter();
    formant2.type = "peaking"; formant2.frequency.value = 2800 + p.formantShift[0] * 120; formant2.Q.value = 1.5;
    formant2.gain.value = Math.abs(p.formantShift[0]) > 0 ? 2 : 0;

    const presence = ctx.createBiquadFilter();
    presence.type = "peaking"; presence.frequency.value = 3500; presence.Q.value = 1.0; presence.gain.value = p.clarity[0];

    const sibilance = ctx.createBiquadFilter();
    sibilance.type = "peaking"; sibilance.frequency.value = 7000; sibilance.Q.value = 2.0; sibilance.gain.value = p.sibilance[0];

    const highShelf = ctx.createBiquadFilter();
    highShelf.type = "highshelf"; highShelf.frequency.value = 5000; highShelf.gain.value = p.brightness[0];

    const compressor = ctx.createDynamicsCompressor();
    const compAmt = Math.max(p.compression[0], p.emphasis[0]);
    compressor.threshold.value = -50 + (100 - compAmt) * 0.4;
    compressor.ratio.value = 1 + compAmt * 0.15;
    compressor.knee.value = 10; compressor.attack.value = 0.003; compressor.release.value = 0.25;

    const waveshaper = ctx.createWaveShaper();
    if (p.raspiness[0] > 0) {
      const amt = p.raspiness[0] / 100;
      const n = 44100;
      const curve = new Float32Array(n);
      for (let i = 0; i < n; i++) { const x = (i * 2) / n - 1; curve[i] = ((1 + amt) * x) / (1 + amt * Math.abs(x)); }
      waveshaper.curve = curve; waveshaper.oversample = "2x";
    }

    const gainNode = ctx.createGain();
    gainNode.gain.value = p.volume[0] / 100;

    let noiseGain = null, noiseSource = null;
    if (p.breathiness[0] > 0) {
      const noiseLen = buffer.length;
      const noiseBuf = ctx.createBuffer(1, noiseLen, buffer.sampleRate);
      const noiseData = noiseBuf.getChannelData(0);
      // Softer base noise amplitude to prevent static
      for (let i = 0; i < noiseLen; i++) noiseData[i] = (Math.random() * 2 - 1) * 0.12;
      noiseSource = ctx.createBufferSource(); noiseSource.buffer = noiseBuf;
      // Shape noise into natural breath: highpass removes rumble, lowpass removes harsh highs
      const noiseHP = ctx.createBiquadFilter();
      noiseHP.type = "highpass"; noiseHP.frequency.value = 500; noiseHP.Q.value = 0.7;
      const noiseLP = ctx.createBiquadFilter();
      noiseLP.type = "lowpass"; noiseLP.frequency.value = 2800; noiseLP.Q.value = 0.7;
      // Gentle peak at breath-like frequencies
      const breathPeak = ctx.createBiquadFilter();
      breathPeak.type = "peaking"; breathPeak.frequency.value = 1600; breathPeak.Q.value = 0.8; breathPeak.gain.value = 3;
      noiseGain = ctx.createGain(); noiseGain.gain.value = p.breathiness[0] / 500;
      noiseSource.connect(noiseHP); noiseHP.connect(noiseLP); noiseLP.connect(breathPeak); breathPeak.connect(noiseGain);
    }

    source.connect(lowShelf); lowShelf.connect(formant1); formant1.connect(formant2);
    formant2.connect(presence); presence.connect(sibilance); sibilance.connect(highShelf);
    highShelf.connect(compressor); compressor.connect(waveshaper); waveshaper.connect(gainNode);
    gainNode.connect(ctx.destination);
    if (noiseGain) noiseGain.connect(ctx.destination);

    return { source, noiseSource, gainNode };
  }, [params]);

  const stopPreview = () => {
    try { sourceRef.current?.stop(); } catch {}
    try { ctxRef.current?.close(); } catch {}
    sourceRef.current = null; ctxRef.current = null; setIsPlaying(false);
  };

  const togglePreview = useCallback(async () => {
    if (isPlaying) { stopPreview(); return; }
    if (!decodedBuffer) { toast.error("No audio loaded"); return; }
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    ctxRef.current = ctx;
    const { source, noiseSource } = buildGraph(ctx, decodedBuffer);
    sourceRef.current = source;
    source.onended = () => setIsPlaying(false);
    source.start(0);
    if (noiseSource) noiseSource.start(0);
    setIsPlaying(true);
  }, [isPlaying, decodedBuffer, buildGraph]);

  useEffect(() => {
    if (!isPlaying || !decodedBuffer) return;
    stopPreview();
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    ctxRef.current = ctx;
    const { source, noiseSource } = buildGraph(ctx, decodedBuffer);
    sourceRef.current = source;
    source.onended = () => setIsPlaying(false);
    source.start(0);
    if (noiseSource) noiseSource.start(0);
    setIsPlaying(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const handleExport = async () => {
    if (!decodedBuffer) return;
    setIsExporting(true);
    try {
      const p = params;
      const pitchRate = Math.pow(2, p.pitch[0] / 12) * p.speed[0];
      const newLen = Math.round(decodedBuffer.length / pitchRate);
      const offCtx = new OfflineAudioContext(decodedBuffer.numberOfChannels, newLen, decodedBuffer.sampleRate);
      const { source, noiseSource } = buildGraph(offCtx, decodedBuffer);
      source.start(0);
      if (noiseSource) noiseSource.start(0);
      const rendered = await offCtx.startRendering();
      const blob = encodeWav(rendered);
      const url = URL.createObjectURL(blob);
      if (onProcessed) onProcessed(url, blob);
      const a = document.createElement("a");
      a.href = url; a.download = `blended-${fileName || "voice"}.wav`; a.click();
      toast.success("Voice blend exported!");
    } catch (e) { console.error("Export error:", e); toast.error("Failed to export"); }
    finally { setIsExporting(false); }
  };

  useEffect(() => () => stopPreview(), []);

  if (!audioUrl) return null;

  const p = params;

  return (
    <div data-testid="voice-blend-controls" className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <span className="text-white/50 text-[10px] font-bold uppercase tracking-wider">Voice Blend</span>
        <div className="flex items-center gap-2">
          <button data-testid="blend-reset" onClick={resetAll}
            className="p-2 rounded-lg bg-white/5 hover:bg-white/8 text-white/25 hover:text-white/50 transition-all">
            <RotateCcw className="w-3.5 h-3.5" />
          </button>
          <button data-testid="blend-preview" onClick={togglePreview}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold transition-all ${
              isPlaying ? "bg-[#FF6B6B]/15 text-[#FF6B6B] border border-[#FF6B6B]/30"
                : "bg-[#1ABC9C]/10 text-[#1ABC9C] border border-[#1ABC9C]/20 hover:bg-[#1ABC9C]/20"
            }`}>
            {isPlaying ? <><Pause className="w-3 h-3" />Stop</> : <><Play className="w-3 h-3" />Preview</>}
          </button>
          <button data-testid="blend-export" onClick={handleExport} disabled={isExporting}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#FFD700]/10 text-[#FFD700] border border-[#FFD700]/20 hover:bg-[#FFD700]/20 text-xs font-bold transition-all disabled:opacity-50">
            {isExporting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
            Export
          </button>
        </div>
      </div>

      {/* Presets */}
      <div className="space-y-2">
        <div className="flex items-center gap-1.5">
          <Zap className="w-3 h-3 text-[#FFD700]" />
          <span className="text-white/35 text-[10px] font-bold uppercase tracking-wider">Presets</span>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {PRESETS.map(preset => (
            <button key={preset.id} data-testid={`preset-${preset.id}`}
              onClick={() => applyPreset(preset)} title={preset.desc}
              className={`px-3 py-1.5 rounded-full text-[11px] font-semibold transition-all border ${
                activePreset === preset.id ? "scale-105 shadow-lg" : "hover:scale-[1.02] opacity-75 hover:opacity-100"
              }`}
              style={{
                backgroundColor: activePreset === preset.id ? `${preset.color}20` : "rgba(255,255,255,0.03)",
                borderColor: activePreset === preset.id ? `${preset.color}50` : "rgba(255,255,255,0.08)",
                color: activePreset === preset.id ? preset.color : "rgba(255,255,255,0.5)",
              }}>
              {preset.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Section title="Timbre" color="#EC4899">
          <Knob label="Formant Shift" value={p.formantShift} onChange={set("formantShift")}
            min={-12} max={12} step={1} unit="st" color="#EC4899" testId="blend-formant" />
          <Knob label="Brightness" value={p.brightness} onChange={set("brightness")}
            min={-12} max={12} step={0.5} unit=" dB" color="#EC4899" testId="blend-brightness" />
          <Knob label="Body / Weight" value={p.bodyWeight} onChange={set("bodyWeight")}
            min={-12} max={12} step={0.5} unit=" dB" color="#EC4899" testId="blend-body" />
        </Section>

        <Section title="Prosody" color="#38BDF8">
          <Knob label="Speed" value={p.speed} onChange={set("speed")}
            min={0.5} max={2.0} step={0.05} unit="x" color="#38BDF8" testId="blend-speed" />
          <Knob label="Emphasis" value={p.emphasis} onChange={set("emphasis")}
            min={0} max={100} step={5} unit="%" color="#38BDF8" testId="blend-emphasis" />
        </Section>

        <Section title="Articulation" color="#F59E0B">
          <Knob label="Presence / Clarity" value={p.clarity} onChange={set("clarity")}
            min={-12} max={12} step={0.5} unit=" dB" color="#F59E0B" testId="blend-clarity" />
          <Knob label="Sibilance" value={p.sibilance} onChange={set("sibilance")}
            min={-12} max={12} step={0.5} unit=" dB" color="#F59E0B" testId="blend-sibilance" />
        </Section>

        <Section title="Pitch" color="#A78BFA">
          <Knob label="Pitch Shift" value={p.pitch} onChange={set("pitch")}
            min={-12} max={12} step={1} unit=" st" color="#A78BFA" testId="blend-pitch" />
        </Section>

        <Section title="Texture" color="#FB923C">
          <Knob label="Breathiness" value={p.breathiness} onChange={set("breathiness")}
            min={0} max={100} step={5} unit="%" color="#FB923C" testId="blend-breathiness" />
          <Knob label="Raspiness" value={p.raspiness} onChange={set("raspiness")}
            min={0} max={100} step={5} unit="%" color="#FB923C" testId="blend-raspiness" />
        </Section>

        <Section title="Dynamics" color="#EF4444">
          <Knob label="Compression" value={p.compression} onChange={set("compression")}
            min={0} max={100} step={5} unit="%" color="#EF4444" testId="blend-compression" />
          <Knob label="Volume" value={p.volume} onChange={set("volume")}
            min={0} max={150} step={5} unit="%" color="#EF4444" testId="blend-volume" />
        </Section>
      </div>
    </div>
  );
}
